import React, { useState, useRef } from 'react';
import { Menu, X, ChevronDown, Sparkles, Globe, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../i18n/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { useUser } from '../../contexts/UserContext';
import { useClickOutside } from './header/useClickOutside';
import { DesktopNav } from './header/DesktopNav';
import { CartDropdown } from './header/CartDropdown';
import { ProfileDropdown } from './header/ProfileDropdown';
import { LanguageDropdown } from './header/LanguageDropdown';
import { MobileMenu } from './header/MobileMenu';
interface HeaderProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}
export function Header({
  onNavigate,
  currentPage
}: HeaderProps) {
  const {
    t,
    language,
    setLanguage,
    languages: langOptions
  } = useLanguage();
  const {
    items,
    removeItem
  } = useCart();
  const {
    user,
    isLoggedIn,
    logout
  } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const cartRef = useRef<HTMLDivElement>(null);
  useClickOutside(profileRef, () => setIsProfileOpen(false));
  useClickOutside(langRef, () => setIsLangOpen(false));
  useClickOutside(cartRef, () => setIsCartOpen(false));
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  const cartTotal = items.reduce((sum, i) => sum + i.price, 0);
  const handleNavClick = (page: string) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    setIsLangOpen(false);
    setIsCartOpen(false);
  };
  const currentLang = langOptions.find(l => l.code === language);
  return <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm" data-id="element-14073">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-14074">
        <div className="flex items-center justify-between h-16" data-id="element-14075">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer group" onClick={() => handleNavClick('home')} data-id="element-14076">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-2.5 shadow-md group-hover:shadow-lg transition-all duration-200" data-id="element-14077">
              <span className="text-white font-bold text-lg" data-id="element-14078">A</span>
            </div>
            <span className="font-bold text-lg text-slate-900 tracking-tight" data-id="element-14079">
              Astriqo
            </span>
          </div>

          {/* Desktop Navigation */}
          <DesktopNav currentPage={currentPage} handleNavClick={handleNavClick} t={t} data-id="element-14080" />

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2" data-id="element-14081">
            <button onClick={() => handleNavClick('free-chart')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-colors ${currentPage === 'free-chart' ? 'text-white bg-indigo-600 shadow-md' : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'}`} data-id="element-14082">
              <Sparkles className="w-3.5 h-3.5" data-id="element-14083" />
              {t('nav.freeChart')}
            </button>

            <div className="relative" ref={langRef} data-id="element-14084">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-14085">
                <Globe className="w-4 h-4" data-id="element-14086" />
                <span className="text-xs font-semibold" data-id="element-14087">
                  {currentLang?.shortLabel}
                </span>
              </button>
              {isLangOpen && <LanguageDropdown langOptions={langOptions} language={language} setLanguage={setLanguage} setIsLangOpen={setIsLangOpen} data-id="element-14088" />}
            </div>

            {/* Cart Button */}
            <div className="relative" ref={cartRef} data-id="element-14089">
              <button onClick={() => setIsCartOpen(!isCartOpen)} className="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors" aria-label="Корзина" data-id="element-14090">
                <ShoppingCart className="w-5 h-5" data-id="element-14091" />
                {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm" data-id="element-14092">
                    {items.length}
                  </span>}
              </button>
              {isCartOpen && <CartDropdown items={items} removeItem={removeItem} formatPrice={formatPrice} cartTotal={cartTotal} onNavigate={onNavigate} setIsCartOpen={setIsCartOpen} data-id="element-14093" />}
            </div>

            {isLoggedIn ? <div className="relative" ref={profileRef} data-id="element-14094">
                <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all bg-white" data-id="element-14095">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center" data-id="element-14096">
                    <span className="text-sm font-bold text-indigo-700 uppercase" data-id="element-14097">
                      {user?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} data-id="element-14098" />
                </button>
                {isProfileOpen && <ProfileDropdown user={user} t={t} handleNavClick={handleNavClick} logout={logout} data-id="element-14099" />}
              </div> : <Button variant="outline" size="sm" onClick={() => handleNavClick('login')} className="hidden lg:flex" data-id="element-14100">
                {t('nav.login') || 'Войти'}
              </Button>}
          </div>

          {/* Mobile cart + menu buttons */}
          <div className="flex items-center gap-1 lg:hidden" data-id="element-14101">
            <button onClick={() => onNavigate('checkout')} className="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Корзина" data-id="element-14102">
              <ShoppingCart className="w-5 h-5" data-id="element-14103" />
              {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm" data-id="element-14104">
                  {items.length}
                </span>}
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Menu" data-id="element-14105">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-14106" /> : <Menu className="w-6 h-6" data-id="element-14107" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu isMobileMenuOpen={isMobileMenuOpen} handleNavClick={handleNavClick} currentPage={currentPage} t={t} user={user} isLoggedIn={isLoggedIn} logout={logout} langOptions={langOptions} language={language} setLanguage={setLanguage} data-id="element-14108" />
    </header>;
}